"use client";

import Image from "next/image";
import React, { useCallback, useState } from "react";

import { urlForImage } from "@/sanity/lib/image";

const TITLE_LINE_COLOR = "#000000";
const SLIDE_DURATION_MS = 500;
const CARD_WIDTH = 300;
const CARD_GAP = 28;
const CARD_MIN_HEIGHT = 320;
const VERIFIED_ICON_URL =
  "https://res.cloudinary.com/dkaghqnvm/image/upload/v1770736316/Trichos/Website/verified_jog3ew.svg";

export interface WReviewsStripReview {
  _id?: string;
  title?: string;
  context?: string;
  tags?: string[];
  stars?: number;
  verified?: boolean;
  name?: string;
  publishedAt?: string;
  avatar?: { asset?: { _ref?: string }; _type?: string };
  avatarUrl?: string;
  sourceUrl?: string;
}

export interface WReviewsStripSectionProps {
  heading?: string;
  maxReviews?: number;
  scoreValue?: string;
  totalReviewCount?: number;
  reviews?: WReviewsStripReview[];
}

function formatDate(input?: string): string {
  if (!input) return "";
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getInitials(name?: string): string {
  if (!name) return "";
  const parts = name
    .split(" ")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  const first = parts[0].charAt(0).toUpperCase();
  const last = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first}${last}`;
}

function StarsDisplay({ stars }: { stars: number }) {
  const n = Math.min(5, Math.max(0, stars));
  if (!n) return null;
  return (
    <span className="inline-flex text-yellow-500" aria-label={`${n} stars`}>
      {"★".repeat(n)}
      {"☆".repeat(5 - n)}
    </span>
  );
}

function ReviewCard({
  review,
  expandedIds,
  onToggleExpand,
}: {
  review: WReviewsStripReview;
  expandedIds: Record<string, boolean>;
  onToggleExpand: (key: string) => void;
}) {
  const key =
    review._id ?? `${review.name ?? "review"}-${review.publishedAt ?? "unknown"}`;
  const isExpanded = Boolean(expandedIds[key]);
  const avatarSrc = review.avatar
    ? urlForImage(review.avatar).width(64).height(64).url()
    : review.avatarUrl ?? null;
  const avatarIsExternal = Boolean(review.avatarUrl && !review.avatar);
  const formattedDate = formatDate(review.publishedAt);
  const initials = getInitials(review.name);

  return (
    <div className="flex flex-col items-center">
      {(avatarSrc || initials) && (
        <div
          className={`relative z-10 -mb-7 flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white shadow-sm ${avatarSrc ? "bg-violet-100" : "bg-violet-200"}`}
          aria-hidden
        >
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              alt=""
              width={56}
              height={56}
              className="object-cover"
              unoptimized={avatarIsExternal}
            />
          ) : (
            <span className="text-sm font-semibold uppercase text-black">
              {initials}
            </span>
          )}
        </div>
      )}
      <article
        className={`group flex h-full w-full flex-1 flex-col items-center justify-start rounded-xl border border-violet-200 bg-white px-4 pb-5 text-center shadow-sm transition-colors duration-200 hover:bg-amber-50 ${avatarSrc || initials ? "pt-9" : "pt-5"}`}
      >
        {review.name && (
          <p className="text-sm font-semibold text-black">{review.name}</p>
        )}
        {formattedDate && (
          <p className="mt-0.5 text-xs text-black">{formattedDate}</p>
        )}
        <div className="mt-2 flex items-center justify-center gap-2">
          {typeof review.stars === "number" && (
            <StarsDisplay stars={review.stars} />
          )}
          {review.verified && (
            <span
              className="relative inline-flex size-5 shrink-0"
              aria-label="Verified"
            >
              <Image
                src={VERIFIED_ICON_URL}
                alt=""
                width={20}
                height={20}
                className="object-contain"
                unoptimized
              />
            </span>
          )}
        </div>
        {review.context && (
          <>
            <div
              className={`mt-3 w-full overflow-hidden transition-all ease-in-out ${
                isExpanded ? "max-h-[600px] duration-700" : "max-h-24 duration-700"
              }`}
            >
              <p className="text-sm leading-relaxed text-black">
                {review.context}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onToggleExpand(key)}
              className="mt-2 text-xs font-semibold text-black hover:underline focus:outline-none focus:underline"
            >
              {isExpanded ? "Hide" : "Read more"}
            </button>
          </>
        )}
      </article>
    </div>
  );
}

export default function WReviewsStripSection({
  heading,
  maxReviews,
  scoreValue,
  totalReviewCount,
  reviews = [],
}: WReviewsStripSectionProps) {
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleToggleExpand = (key: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const displayHeading =
    heading != null && String(heading).trim() !== ""
      ? String(heading).trim()
      : "";

  const visibleReviews =
    reviews && reviews.length
      ? reviews.slice(0, maxReviews && maxReviews > 0 ? maxReviews : reviews.length)
      : [];

  const length = visibleReviews.length;
  const goNext = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.min(length - 1, i + 1));
  }, [length]);

  const goPrev = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [length]);

  const slideTransition = `${SLIDE_DURATION_MS}ms ease-out`;
  const arrowButtonClass =
    "flex shrink-0 items-center justify-center p-1 text-black transition-opacity hover:opacity-80 focus:outline-none focus:ring-0 disabled:opacity-40";

  if (!displayHeading && !scoreValue && !totalReviewCount && visibleReviews.length === 0)
    return null;

  return (
    <section
      className="mx-auto w-full max-w-6xl py-6 md:py-8"
      aria-label={displayHeading || "Reviews"}
    >
      <div className="mx-auto w-full px-4 md:px-6">
        {displayHeading && (
          <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${TITLE_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-black md:text-xl lg:text-2xl">
              {displayHeading}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${TITLE_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}

        {visibleReviews.length > 0 && (
          <div className="mx-auto w-full rounded-xl p-3 md:p-4">
            <div className="flex items-stretch gap-2 md:gap-3">
              <button
                type="button"
                onClick={goPrev}
                className={arrowButtonClass}
                aria-label="Previous review"
                disabled={length <= 1}
              >
                <svg
                  className="size-8 md:size-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Mobile: one card at a time */}
              <div className="relative min-w-0 flex-1 overflow-hidden md:hidden">
                <div
                  className="flex"
                  style={{
                    transition: `transform ${slideTransition}`,
                    transform: `translateX(-${currentIndex * 100}%)`,
                    minHeight: CARD_MIN_HEIGHT,
                  }}
                >
                  {visibleReviews.map((review) => {
                    const key =
                      review._id ?? `${review.name ?? "review"}-${review.publishedAt ?? "unknown"}`;
                    return (
                      <div
                        key={key}
                        className="min-w-full flex-shrink-0 px-1"
                        style={{ minHeight: CARD_MIN_HEIGHT }}
                      >
                        <ReviewCard
                          review={review}
                          expandedIds={expandedIds}
                          onToggleExpand={handleToggleExpand}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop: fixed-width cards */}
              <div className="relative hidden min-w-0 flex-1 overflow-hidden md:block">
                <div
                  className="flex justify-center"
                  style={{ minHeight: CARD_MIN_HEIGHT }}
                >
                  <div
                    className="flex"
                    style={{
                      transition: `transform ${slideTransition}`,
                      transform: `translateX(-${currentIndex * (CARD_WIDTH + CARD_GAP)}px)`,
                      gap: CARD_GAP,
                    }}
                  >
                    {visibleReviews.map((review) => {
                      const key =
                        review._id ?? `${review.name ?? "review"}-${review.publishedAt ?? "unknown"}`;
                      return (
                        <div
                          key={key}
                          className="flex flex-shrink-0 flex-col items-center"
                          style={{
                            width: CARD_WIDTH,
                            minHeight: CARD_MIN_HEIGHT,
                          }}
                        >
                          <ReviewCard
                            review={review}
                            expandedIds={expandedIds}
                            onToggleExpand={handleToggleExpand}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={goNext}
                className={arrowButtonClass}
                aria-label="Next review"
                disabled={length <= 1}
              >
                <svg
                  className="size-8 md:size-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {(scoreValue || typeof totalReviewCount === "number") && (
          <div className="mt-6 text-center text-sm text-black md:text-base">
            <span className="font-medium">
              Google rating score:
              {scoreValue ? (
                <span className="font-bold"> {scoreValue}</span>
              ) : (
                <span className="font-bold"> N/A</span>
              )}{" "}
              of 5
            </span>
            {typeof totalReviewCount === "number" && (
              <span>
                , based on{" "}
                <span className="font-semibold">{totalReviewCount}</span> reviews
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

